<template>
  <RouterLink v-if="'route' in item" :to="item.route" :class="cls">
    <Template.Use />
  </RouterLink>
  <button
    v-else-if="'action' in item"
    type="button"
    :class="cls"
    @click="item.action()"
  >
    <Template.Use />
  </button>

  <Template.Define>
    <component
      :is="item.icon"
      aria-hidden
      class="size-4 shrink-0 text-gray-500 transition dark:text-muted-foreground"
    />

    <span>{{ item.title }}</span>
  </Template.Define>
</template>

<script setup lang="ts">
import { computed, normalizeClass } from "vue";
import { RouterLink, useRouter } from "vue-router/auto";

import { createReusableTemplate } from "@/lib/composables/template";
import type { AnyRouteConfiguration } from "@/search/routes";

const router = useRouter();

const Template = createReusableTemplate();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  item: AnyRouteConfiguration;
  active?: boolean;
}>();

const isRouteActive = computed(() => {
  if ("route" in props.item) {
    const to = props.item.route;
    if (!to || !("name" in to) || !to.name) {
      return false;
    }

    const currentRoute = router.currentRoute.value;
    const currentName = currentRoute.name;
    const currentPath = currentRoute.path;
    const targetName = to.name;

    // First try to match by route name (most reliable, works in iframes)
    if (currentName && typeof currentName === "string") {
      // Check if current route name matches the target name exactly
      if (currentName === targetName) {
        // If query params are specified, they must match exactly
        if (to.query) {
          return (
            JSON.stringify(currentRoute.query) === JSON.stringify(to.query)
          );
        }
        return true;
      }

      // Check if current route name starts with target name (for parent/child routes)
      // e.g., /emails.[id] should match parent route /emails
      // e.g., /emails.[id]/details should match parent route /emails.[id]
      if (
        currentName.startsWith(targetName + ".") ||
        currentName.startsWith(targetName + "/")
      ) {
        if (to.query) {
          return (
            JSON.stringify(currentRoute.query) === JSON.stringify(to.query)
          );
        }
        return true;
      }
    }

    // Fallback to path matching (for cases where route name isn't available)
    // Convert route name pattern to path pattern for matching
    // e.g., /emails.[id] -> /emails/
    const pathPattern = targetName.replace(/\[.*?\]/g, "").replace(/\./g, "/");
    if (currentPath.startsWith(pathPattern)) {
      if (to.query) {
        return JSON.stringify(currentRoute.query) === JSON.stringify(to.query);
      }
      return true;
    }

    return false;
  }

  return false;
});

const cls = computed(() => {
  return normalizeClass([
    `mt-px flex shrink-0 select-none items-center gap-2 truncate rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 outline-2 outline-blue-500 transition focus-visible:outline dark:text-white`,
    !isRouteActive.value && !props.active
      ? `hover:bg-gray-300/50 active:bg-gray-300/50 dark:hover:bg-muted dark:active:bg-card`
      : `bg-gray-300/50 dark:bg-muted`,
  ]);
});
</script>
